import React, { useState, useEffect, useContext } from 'react';
import GettingItDoneDIY from './GettingItDoneDIY/GettingItDoneDIY';
import GettingItDoneProfessional from './GettingItDoneProfessional/GettingItDoneProfessional';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import { Play } from 'lucide-react';
import ResourceCard from '/src/Components/ResourceCard/ResourceCard';
import { ThumbtackStorage } from '/src/services/QGDStorage';
import { AddressSearchContext, WindowSizeContext } from '/src/App';
import './style.css';

const SECTION_HEADER = "How to Get This Project Done";

const ProjectGettingItDone = ({ propertyData, ttListingsLoaded, project }) => {

    const { deviceType } = useContext(WindowSizeContext);

    // Get the portalData from the AddressSearchContext
    const { portalData } = useContext(AddressSearchContext);

    // Load data from local storage with safety checks
    const project_skus_local_storage = portalData.skus;
    const companies_local_storage = portalData.companies;
    const icons_local_storage = portalData.icons;
    const resources_local_storage = portalData.resources;


    const [selectedPath, setSelectedPath] = useState(null);
    const [ttListings, setTtListings] = useState([]);

    // useEffect that lists to ttListingsLoaded and if true loads ttListings from indexedDB
    useEffect(() => {
        async function fetchStoredListings() {
            if (ttListingsLoaded) {
                const zip_code = propertyData.data.property_details.zip;
                const project_id = project.project_id;

                try {
                    const listingsObject = await ThumbtackStorage.getListing(zip_code, project_id);
                    if (listingsObject) {
                        console.log('Found stored Thumbtack listings:', listingsObject);
                        const listings = listingsObject.listings || [];
                        if (listings.length > 0) {
                            setTtListings(listings);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching Thumbtack listings from storage:', error);
                }
            }
        }

        fetchStoredListings();
    }, [ttListingsLoaded, propertyData, project]);

    console.log('ttListings', ttListings);

    const labor_costs = project.project_cost_labor;
    const diy_friendly = project.project_diy_friendly;

    // Determine the intro sentence based on conditions
    let intro_sentence;
    if (!diy_friendly) {
        intro_sentence = "This project is not particularly DIY friendly so unless home improvement is your thing, we'd recommend hiring a professional. That said, if you want to take this on we have resources to help!";
    } else if (diy_friendly && labor_costs < 10) {
        intro_sentence = "This project is not only DIY friendly, we think it's a perfect candidate for it!";
    } else if (diy_friendly && labor_costs >= 10) {
        intro_sentence = "This project is DIY friendly and many homeowners may choose that path. But it could be worth considering hiring somebody.";
    }

    const chooseOptionText = "Choose an option below to learn more about how to get this project done."

    const fullIntroText = intro_sentence + " " + chooseOptionText;

    const diyImage = "diy-home-cartoon.webp"
    const professionalImage = "home-remodeling-services.webp"

    const pathOptions = [
        {
            id: 'diy',
            label: 'Doing it yourself...',
            image: CreateCloudfrontUrl(diyImage)
        },
        {
            id: 'professional',
            label: 'Hiring a professional...',
            image: CreateCloudfrontUrl(professionalImage)
        }
    ]

    const handlePathClick = (pathId) => {
        setSelectedPath(pathId);
    }

    // Get the resources from the project with safety check
    const project_resources = project.project_associated_resources || [];

    // Get the project house category and associated climate risks with safety checks
    const project_home_category = project.project_home_category || '';
    const project_climate_risks_refined = (project.project_associated_climate_risks || [])
        .map(risk => risk?.risk_name)
        .filter(Boolean);

    // First, get directly associated resources
    const direct_resources = project_resources
        .map(resource_id => resources_local_storage.find(r => r?.id === resource_id))
        .filter(Boolean);

    // If we need more resources, find additional relevant ones
    let additional_resources = [];
    if (direct_resources.length < 4) {
        // First attempt: climate risk matching
        additional_resources = resources_local_storage
            .filter(resource => {
                if (!resource) return false;

                const isDuplicate = direct_resources.some(dr => dr?.id === resource.id);
                if (isDuplicate) return false;

                const isNotTakingAction = resource.sub_category !== "Taking Action";
                const matchesHomeCategory = Array.isArray(resource.associated_house_categories) &&
                    resource.associated_house_categories.includes(project_home_category);
                const hasMatchingClimateRisk = Array.isArray(resource.associated_climate_risks) &&
                    resource.associated_climate_risks.some(
                        risk => project_climate_risks_refined.includes(risk)
                    );

                return isNotTakingAction && matchesHomeCategory && hasMatchingClimateRisk;
            });

        // If still need more resources, try second criteria
        if ((direct_resources.length + additional_resources.length) < 4) {
            const more_resources = resources_local_storage
                .filter(resource => {
                    if (!resource) return false;

                    // Check for duplicates in both direct and first additional resources
                    const isDuplicate = [...direct_resources, ...additional_resources]
                        .some(dr => dr?.id === resource.id);
                    if (isDuplicate) return false;

                    // Check home category match
                    const matchesHomeCategory = Array.isArray(resource.associated_house_categories) &&
                        resource.associated_house_categories.includes(project_home_category);

                    // Check resource type or sub-category criteria
                    const matchesTypeOrCategory =
                        resource.resource_type === "Maintenance" ||
                        resource.sub_category === "Know the Risk" ||
                        resource.sub_category === "Being Prepared";

                    return matchesHomeCategory && matchesTypeOrCategory;
                });

            // Add these new resources to additional_resources
            additional_resources = [...additional_resources, ...more_resources];
        }
    }

    // Combine and limit to 4 resources
    const enriched_resources = [
        ...direct_resources,
        ...additional_resources
    ].slice(0, 4);

    console.log('Enriched Resources:', enriched_resources);

    const selectedPathHeader = selectedPath === 'diy' ? 'Doing it Yourself' : 'Hiring a Professional';



    return (
        <div className={`project-card-section-container ${deviceType}`}>
            <div className={`project-card-section-header-frame ${deviceType}`}>
                <h2
                    className={`project-card-section-header ${deviceType} ${selectedPath ? 'project-card-section-header--selected' : ''} box-shadow-standard`}
                    onClick={selectedPath ? () => setSelectedPath(null) : undefined}
                >
                    {
                        selectedPath ?
                            (
                                <>
                                    {selectedPathHeader}
                                    <button
                                        className="path-option-change-path-button box-shadow-standard"
                                        onClick={() => setSelectedPath(null)}
                                    >
                                        Change
                                    </button>
                                </>
                            ) :
                            (
                                SECTION_HEADER
                            )
                    }
                </h2>

                {!selectedPath && (
                    <p className="project-card-section-intro-sentence text-align-center">{fullIntroText}</p>
                )}
            </div>
            <div className={`project-card-getting-it-done-frame `}>

                {/* {selectedPath && (
                    <>
                        <div className="project-card-getting-it-done-left-column">
                            <div className="selected-path-container">
                                <button
                                    className="path-option-button path-option-button--selected"
                                >
                                    <div className="path-option-image-container">
                                        <img
                                            src={pathOptions.find(option => option.id === selectedPath).image}
                                            alt={pathOptions.find(option => option.id === selectedPath).label}
                                            className="path-option-image"
                                        />
                                        <div className="path-option-overlay"></div>
                                        <span className="path-option-label">
                                            {pathOptions.find(option => option.id === selectedPath).label}
                                        </span>
                                    </div>
                                </button>
                                <button
                                    className="path-option-change-path-button box-shadow-standard"
                                    onClick={() => setSelectedPath(null)}
                                >
                                    Change
                                </button>
                            </div>
                        </div>
                    </>
                )} */}
                <div className="project-card-getting-it-done-right-column">
                    {!selectedPath && (
                        <>
                            <div className={`path-options-container ${deviceType}`}>
                                {pathOptions.map((option) => (
                                    <button
                                        key={option.id}
                                        className={`path-option-button ${selectedPath === option.id ? 'path-option-button--selected' : ''} box-shadow-standard`}
                                        onClick={() => handlePathClick(option.id)}
                                    >
                                        <div className="path-option-image-container">
                                            <img
                                                src={option.image}
                                                alt={option.label}
                                                className="path-option-image"
                                            />
                                            <div className="path-option-overlay"></div>
                                            <span className="path-option-label">{option.label}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                    {selectedPath === 'diy' && <GettingItDoneDIY project={project} />}
                    {selectedPath === 'professional' && <GettingItDoneProfessional thumbtackListings={ttListings} project={project} />}
                </div>

            </div>
        </div>
    );
};

export default ProjectGettingItDone;